import { useEffect } from 'react';

const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 'https://lordofthequiz.com.br';
const DEFAULT_IMAGE = `${SITE_URL}/og-default.jpg`;

const routeMeta = {
  '/': {
    title: 'Lord of the Quiz | O Melhor Quiz Show do Brasil',
    description: 'Participe do melhor Quiz Show do Brasil! Eventos presenciais, jogos online, prêmios incríveis e diversão garantida.',
  },
  '/eventos': {
    title: 'Eventos | Lord of the Quiz',
    description: 'Confira os próximos eventos de Quiz Show do Brasil. Compre ingressos, veja locais e horários.',
  },
  '/jogar': {
    title: 'Jogar Online | Lord of the Quiz',
    description: 'Jogue quiz online e teste seus conhecimentos!',
  },
  '/clube': {
    title: 'Quiz Club | Lord of the Quiz',
    description: 'Assine o Quiz Club e tenha acesso a beneficios exclusivos.',
  },
  '/placar': {
    title: 'Placar | Lord of the Quiz',
    description: 'Veja o ranking dos melhores jogadores do Lord of the Quiz.',
  },
  '/temas': {
    title: 'Alertas de Temas | Lord of the Quiz',
    description: 'Curta seus temas favoritos e ative alertas para receber avisos por e-mail e notificação quando eles entrarem na agenda.',
  },
  '/perguntas': {
    title: 'Sugerir Perguntas | Lord of the Quiz',
    description: 'Sugira perguntas para os proximos quizzes!',
  },
  '/contratar': {
    title: 'Contratar | Lord of the Quiz',
    description: 'Contrate o Lord of the Quiz para seu evento.',
  },
  '/sobre': {
    title: 'Sobre | Lord of the Quiz',
    description: 'Conheca a historia do Lord of the Quiz.',
  }
};

function setMeta(property, content, isName = false) {
  const attr = isName ? 'name' : 'property';
  let el = document.querySelector(`meta[${attr}="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Global flag so useSiteSettings knows not to overwrite page-specific SEO
window.__seoPageActive = false;

export function SEO({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  event = null,
  pathname = '/'
}) {
  const routeDefaults = routeMeta[pathname] || routeMeta['/'];
  
  const finalTitle = String(title || routeDefaults.title || 'Lord of the Quiz');
  const finalDescription = String(description || routeDefaults.description || '');
  const finalImage = String(image || DEFAULT_IMAGE);
  const finalUrl = String(url || `${SITE_URL}${pathname}`);
  const finalType = String(type || 'website');

  useEffect(() => {
    // Signal that a page-specific SEO is active
    window.__seoPageActive = true;
    
    // Title
    document.title = finalTitle;
    
    // Meta description
    setMeta('description', finalDescription, true);
    
    // Open Graph
    setMeta('og:title', finalTitle);
    setMeta('og:description', finalDescription);
    setMeta('og:image', finalImage);
    setMeta('og:url', finalUrl);
    setMeta('og:type', finalType);
    setMeta('og:site_name', 'Lord of the Quiz');
    setMeta('og:locale', 'pt_BR');
    
    // Twitter Card
    setMeta('twitter:card', 'summary_large_image', true);
    setMeta('twitter:title', finalTitle, true);
    setMeta('twitter:description', finalDescription, true);
    setMeta('twitter:image', finalImage, true);
    
    // Canonical
    setCanonical(finalUrl);
    
    // JSON-LD
    let scriptEl = document.getElementById('seo-jsonld');
    if (event) {
      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": event.title,
        "description": event.description,
        "image": event.image_url || DEFAULT_IMAGE,
        "url": finalUrl,
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": event.event_type === "online" 
          ? "https://schema.org/OnlineEventAttendanceMode"
          : "https://schema.org/OfflineEventAttendanceMode",
        "location": {
          "@type": "Place",
          "name": event.location || event.venue?.name || "Local a confirmar",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "BR"
          }
        },
        "organizer": {
          "@type": "Organization",
          "name": "Lord of the Quiz",
          "url": SITE_URL
        }
      };
      
      if (event.date && event.time) {
        jsonLd.startDate = `${event.date}T${event.time}:00`;
      }
      
      if (event.price > 0) {
        jsonLd.offers = {
          "@type": "Offer",
          "price": String(event.price),
          "priceCurrency": "BRL",
          "url": finalUrl,
          "availability": "https://schema.org/InStock"
        };
      }
      
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = 'seo-jsonld';
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    } else if (scriptEl) {
      scriptEl.remove();
    }
    
    return () => {
      // Cleanup JSON-LD and flag on unmount
      window.__seoPageActive = false;
      const el = document.getElementById('seo-jsonld');
      if (el) el.remove();
    };
  }, [finalTitle, finalDescription, finalImage, finalUrl, finalType, event]);

  // No JSX needed - all DOM manipulation
  return null;
}

export default SEO;
